import React, { useContext, useRef } from 'react';
import styled from 'styled-components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import Tooltip from '@ubeya/shared-web/components/Tooltip/Tooltip';
import ToggleWithIcon from '@ubeya/shared-web/components/Toggle/ToggleWithIcon';
import { ReactComponent as FileUploadIcon } from '@ubeya/shared-web/assets/help-desk/file-upload.svg';
import { ReactComponent as EyeHideIcon } from '@ubeya/shared-web/assets/eye-hide.svg';
import { ReactComponent as MentionIcon } from '@ubeya/shared-web/assets/help-desk/mention.svg';
import { ReactComponent as SendChatIcon } from '@ubeya/shared-web/assets/help-desk/send-chat.svg';
import {
  ALLOWED_IMAGE_EXTENSIONS,
  ALLOWED_VIDEO_EXTENSIONS,
  ALLOWED_OTHER_EXTENSIONS
} from 'helpDesk/helpDeskConstants';
import { toAcceptAttribute } from 'helpDesk/helpDeskUtils';

const ATTACHMENT_ACCEPT = toAcceptAttribute([
  ...ALLOWED_IMAGE_EXTENSIONS,
  ...ALLOWED_VIDEO_EXTENSIONS,
  ...ALLOWED_OTHER_EXTENSIONS
]);

const ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ToolbarLeft = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  gap: 16px;
`;

const InternalNoteToggleWrap = styled(ToggleWithIcon)`
  div:first-child {
    background: ${({ value, theme }) => (value ? theme.colors.orangeChanges + '30' : theme.colors.grey2)};
  }
  div:first-child > div {
    background-color: ${({ value, theme }) => (value ? theme.colors.orangeChanges : theme.colors.white)};
    padding: 2px;
    svg {
      color: ${({ value, theme }) => (value ? theme.colors.white : theme.colors.grey4)};
      path {
        fill: ${({ value, theme }) => (value ? theme.colors.white : theme.colors.grey4)};
      }
    }
  }
`;

const Divider = styled.div`
  width: 1px;
  height: 24px;
  background: ${({ theme }) => theme.colors.grey2};
`;

const AttachFileIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;

  svg {
    width: 20px;
    height: 20px;
  }

  path {
    fill: ${({ theme }) => theme.colors.grey4};
  }

  &:hover {
    opacity: 0.8;
  }
`;

const MentionIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;

  svg {
    width: 20px;
    height: 20px;
  }

  path {
    stroke: ${({ theme }) => theme.colors.grey4};
  }

  &:hover {
    opacity: 0.8;
  }
`;

const SendIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  background: ${({ $isInternal, theme, disabled }) =>
    disabled ? theme.colors.disabled : $isInternal ? theme.colors.orangeChanges : theme.colors.primary};
  color: ${({ theme, disabled }) => (disabled ? theme.colors.grey3 : theme.colors.white)};
  svg {
    fill: ${({ theme, disabled }) => (disabled ? theme.colors.grey3 : theme.colors.white)};
  }

  path {
    fill: ${({ theme, disabled }) => (disabled ? theme.colors.grey3 : theme.colors.white)};
  }

  &:hover:not(:disabled) {
    opacity: 0.85;
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ComposerToolbar = ({
  isInternalNote,
  onInternalNoteToggle,
  onFilesSelected,
  onMentionClick,
  canSend,
  isSending
}) => {
  const { t } = useContext(ConfigContext);
  const fileInputRef = useRef(null);

  return (
    <ToolbarRow>
      <ToolbarLeft>
        <AttachFileIconButton
          type="button"
          onClick={() => fileInputRef.current?.click()}
          title={t('help_desk_attach_file')}
          aria-label={t('help_desk_attach_file')}>
          <FileUploadIcon />
        </AttachFileIconButton>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ATTACHMENT_ACCEPT}
          style={{ display: 'none' }}
          onChange={onFilesSelected}
        />

        <Divider />

        <InternalNoteToggleWrap
          value={isInternalNote}
          onChange={onInternalNoteToggle}
          Icon={EyeHideIcon}
          tooltipText={t('help_desk_internal_note')}
        />

        {isInternalNote && (
          <Tooltip body={t('help_desk_mention')}>
            <MentionIconButton type="button" onClick={onMentionClick} aria-label={t('help_desk_mention')}>
              <MentionIcon />
            </MentionIconButton>
          </Tooltip>
        )}
      </ToolbarLeft>

      <SendIconButton type="submit" $isInternal={isInternalNote} disabled={!canSend || isSending}>
        <SendChatIcon style={{ width: 20, height: 20 }} />
      </SendIconButton>
    </ToolbarRow>
  );
};

export default ComposerToolbar;
